import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../lib/utils';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from '@/components/ui/field';

type SwitchSize = 'small' | 'medium';
type SwitchContext = 'default' | 'modal';
type SwitchChangeEvent = React.ChangeEvent<HTMLInputElement>;

const mergeIds = (...values: Array<string | undefined>): string | undefined => {
  const ids = values.flatMap(
    (value) => value?.split(/\s+/).filter(Boolean) ?? []
  );

  return ids.length > 0 ? Array.from(new Set(ids)).join(' ') : undefined;
};

interface SwitchProps extends Omit<
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
  'onChange'
> {
  onChange?: (event: SwitchChangeEvent, checked: boolean) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  size?: SwitchSize;
  disableRipple?: boolean;
  disableFocusRipple?: boolean;
  context?: SwitchContext;
  label?: React.ReactNode;
  description?: React.ReactNode;
  fieldClassName?: string;
  fieldContentClassName?: string;
  labelClassName?: string;
  descriptionClassName?: string;
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(
  (
    {
      className,
      onChange,
      onCheckedChange,
      inputProps,
      size = 'medium',
      context = 'default',
      disableRipple,
      disableFocusRipple,
      label,
      description,
      fieldClassName,
      fieldContentClassName,
      labelClassName,
      descriptionClassName,
      id,
      name,
      value,
      required,
      checked,
      defaultChecked,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      ...props
    },
    ref
  ) => {
    void disableRipple;
    void disableFocusRipple;
    const generatedId = React.useId();

    const descriptionId = description
      ? `ui-switch-description-${generatedId.replace(/:/g, '')}`
      : undefined;

    const hasFieldText = Boolean(label || description);

    const inputRootProps = inputProps as unknown as
      React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> | undefined;

    const resolvedChecked = checked ?? inputProps?.checked;
    const resolvedDefaultChecked = defaultChecked ?? inputProps?.defaultChecked;
    const isControlled = typeof resolvedChecked === 'boolean';

    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
      Boolean(resolvedDefaultChecked)
    );

    const isChecked = isControlled
      ? Boolean(resolvedChecked)
      : uncontrolledChecked;

    const resolvedId =
      id ??
      inputProps?.id ??
      (hasFieldText ? `ui-switch-${generatedId.replace(/:/g, '')}` : undefined);

    const resolvedName = name ?? inputProps?.name;

    const resolvedValue =
      value ??
      (inputProps?.value !== undefined ? String(inputProps.value) : undefined);

    const resolvedRequired = required ?? inputProps?.required;
    const resolvedAriaLabel = ariaLabel ?? inputProps?.['aria-label'];

    const resolvedAriaLabelledby =
      ariaLabelledby ??
      inputProps?.['aria-labelledby'] ??
      (!label && description ? descriptionId : undefined);

    const resolvedAriaDescribedby = mergeIds(
      inputProps?.['aria-describedby'],
      ariaDescribedby,
      descriptionId
    );

    const createChangeEvent = React.useCallback(
      (nextChecked: boolean): SwitchChangeEvent => {
        const target = {
          checked: nextChecked,
          id: resolvedId,
          name: resolvedName,
          value: resolvedValue,
          required: resolvedRequired,
          ...(inputProps ?? {}),
        } as unknown as HTMLInputElement;

        return {
          target,
          currentTarget: target,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as SwitchChangeEvent;
      },
      [inputProps, resolvedId, resolvedName, resolvedRequired, resolvedValue]
    );

    const switchControl = (
      <SwitchPrimitive.Root
        {...inputRootProps}
        ref={ref}
        id={resolvedId}
        name={resolvedName}
        required={resolvedRequired}
        value={resolvedValue}
        checked={resolvedChecked}
        defaultChecked={resolvedDefaultChecked}
        onCheckedChange={(nextChecked) => {
          if (!isControlled) {
            setUncontrolledChecked(nextChecked);
          }
          onCheckedChange?.(nextChecked);
          onChange?.(createChangeEvent(nextChecked), nextChecked);
        }}
        className={cn(
          'ui-switch peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent [--switch-thumb-size:1.25rem] [--switch-thumb-translate:1.25rem] transition-colors outline-none focus-visible:border-[var(--color-ring-default)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring-default)] disabled:cursor-not-allowed disabled:opacity-50',
          isChecked
            ? '!bg-status-success'
            : 'bg-[var(--surface-toggle-track,var(--surface-muted))]',
          context === 'modal' && !isChecked && 'bg-[var(--color-neutral-0-30)]',
          size === 'small' &&
            'h-5 w-[2.125rem] [--switch-thumb-size:1rem] [--switch-thumb-translate:0.875rem]',
          size === 'medium' &&
            'h-6 w-11 [--switch-thumb-size:1.25rem] [--switch-thumb-translate:1.25rem]',
          className
        )}
        {...props}
        aria-label={resolvedAriaLabel}
        aria-labelledby={resolvedAriaLabelledby}
        aria-describedby={resolvedAriaDescribedby}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'ui-switch-thumb block h-[var(--switch-thumb-size)] w-[var(--switch-thumb-size)] rounded-full bg-[var(--color-neutral-25)] shadow-sm ring-0 transition-transform data-[state=unchecked]:translate-x-0 data-[state=checked]:translate-x-[var(--switch-thumb-translate)]'
          )}
        />
      </SwitchPrimitive.Root>
    );

    if (!hasFieldText) {
      return switchControl;
    }

    return (
      <Field
        orientation="horizontal"
        className={cn('gap-3', !description && 'items-center', fieldClassName)}
      >
        {switchControl}
        <FieldContent className={fieldContentClassName}>
          {label ? (
            <FieldLabel
              htmlFor={resolvedId}
              className={cn(
                'flex min-h-6 items-center',
                size === 'small' ? 'text-sm' : 'text-base',
                context === 'modal' && 'text-[var(--color-neutral-25)]',
                labelClassName
              )}
            >
              {label}
            </FieldLabel>
          ) : null}
          {description ? (
            <FieldDescription
              id={descriptionId}
              className={cn(
                '![margin-block-start:0] ![margin-block-end:0]',
                size === 'small' && 'text-xs',
                context === 'modal' && 'text-[var(--color-neutral-25)]/80',
                descriptionClassName
              )}
            >
              {description}
            </FieldDescription>
          ) : null}
        </FieldContent>
      </Field>
    );
  }
);

Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
